import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Building2, Mail, Lock, User, Phone, Briefcase, ArrowRight, Building, Users, CreditCard } from 'lucide-react';
import api from '../lib/api';

const TEAL = '#00a99d';
const DARK = '#1a1a2e';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan');
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '', organisationName: '' });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register({ email: formData.email, password: formData.password, name: formData.name, phone: formData.phone, role: 'LANDLORD', organisation_name: formData.organisationName || undefined });
      toast.success('Account created!');
      if (selectedPlan) {
        try {
          const params = new URLSearchParams({ tier: selectedPlan, origin_url: window.location.origin });
          const res = await api.post(`/subscriptions/checkout?${params}`);
          if (res.data.url) { window.location.href = res.data.url; return; }
        } catch { /* fall through */ }
      }
      navigate('/landlord/subscription');
    } catch (error) {
      const code = error?.code || '';
      const msg = code.includes('email-already-in-use') ? 'Account already exists. Sign in instead.' : code.includes('weak-password') ? 'Password too weak (min 6 chars).' : error.response?.data?.detail || 'Registration failed.';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const planLabel = selectedPlan ? { BASIC: 'Basic ($29/mo)', PRO: 'Pro ($59/mo)', EXPERT: 'Expert ($99/mo)', ENTERPRISE: 'Enterprise ($299/mo)' }[selectedPlan] : null;

  const inputStyle = {
    width: '100%', height: 46, padding: '0 16px 0 46px', borderRadius: 10,
    border: '1px solid #e5e7eb', fontSize: 15, color: DARK, background: '#fff', outline: 'none', transition: 'border-color 0.2s',
  };

  const fields = [
    { name: 'name', label: 'Full Name', icon: User, placeholder: 'John Doe', type: 'text', required: true, testId: 'register-name-input' },
    { name: 'organisationName', label: 'Company Name', icon: Briefcase, placeholder: 'My Holdings LLC', type: 'text', required: false, testId: 'register-org-name-input' },
    { name: 'email', label: 'Email', icon: Mail, placeholder: 'you@example.com', type: 'email', required: true, testId: 'register-email-input' },
    { name: 'phone', label: 'Phone (Optional)', icon: Phone, placeholder: '+1 (555) 000-0000', type: 'tel', required: false, testId: 'register-phone-input' },
    { name: 'password', label: 'Password', icon: Lock, placeholder: 'Min 6 characters', type: 'password', required: true, testId: 'register-password-input' },
    { name: 'confirmPassword', label: 'Confirm Password', icon: Lock, placeholder: 'Confirm password', type: 'password', required: true, testId: 'register-confirm-password-input' },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="register-page">
      {/* Left Panel */}
      <div className="hidden lg:flex" style={{ width: '42%', background: DARK, flexDirection: 'column', justifyContent: 'center', padding: '48px 56px' }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
            <Building2 style={{ width: 32, height: 32, color: TEAL }} />
            <span style={{ fontSize: 26, fontWeight: 700, color: '#fff' }}>Realty <span style={{ color: TEAL }}>JAG</span></span>
          </div>
          <p style={{ fontSize: 22, fontWeight: 600, color: '#fff', lineHeight: 1.4, marginBottom: 8 }}>Start managing properties the smart way</p>
          <p style={{ fontSize: 15, color: '#94a3b8', lineHeight: 1.6 }}>Join hundreds of landlords who trust Realty JAG to streamline their rental business.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {[
            { icon: Building, num: '500+', label: 'Properties Managed' },
            { icon: Users, num: '2,000+', label: 'Units Tracked' },
            { icon: CreditCard, num: '$5M+', label: 'Rent Collected' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(0,169,157,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.icon style={{ width: 20, height: 20, color: TEAL }} />
              </div>
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: 0 }}>{s.num}</p>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, background: '#fff', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 420, paddingTop: 16, paddingBottom: 16 }}>
          <div className="lg:hidden" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
            <Building2 style={{ width: 28, height: 28, color: TEAL }} />
            <span style={{ fontSize: 22, fontWeight: 700, color: DARK }}>Realty <span style={{ color: TEAL }}>JAG</span></span>
          </div>

          <h2 style={{ fontSize: 28, fontWeight: 700, color: DARK, marginBottom: 8 }}>Create Account</h2>
          {planLabel ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
              <span style={{ fontSize: 14, color: '#64748b' }}>Selected plan:</span>
              <span data-testid="selected-plan-badge" style={{ fontSize: 13, fontWeight: 600, color: TEAL, background: `${TEAL}10`, padding: '3px 12px', borderRadius: 50 }}>{planLabel}</span>
            </div>
          ) : (
            <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>You'll choose a plan after creating your account.</p>
          )}

          <form onSubmit={handleSubmit}>
            {fields.map(f => (
              <div key={f.name} style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>{f.label}</label>
                <div style={{ position: 'relative' }}>
                  <f.icon style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 17, height: 17, color: '#94a3b8' }} />
                  <input
                    name={f.name} type={f.type} value={formData[f.name]} onChange={handleChange}
                    placeholder={f.placeholder} required={f.required} data-testid={f.testId}
                    style={inputStyle} onFocus={e => e.target.style.borderColor = TEAL} onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
            ))}
            <button type="submit" disabled={loading} data-testid="register-submit-btn" style={{
              width: '100%', height: 48, borderRadius: 10, border: 'none', marginTop: 8,
              background: TEAL, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: loading ? 0.7 : 1,
            }}>
              {loading ? 'Creating...' : selectedPlan ? (<>Create & Subscribe <ArrowRight style={{ width: 16, height: 16 }} /></>) : 'Create Account'}
            </button>
          </form>
          <p style={{ marginTop: 24, textAlign: 'center', fontSize: 14, color: '#64748b' }}>
            Already have an account? <Link to="/login" style={{ color: TEAL, fontWeight: 600, textDecoration: 'none' }} data-testid="login-link">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
